<script>
  import { onMount } from "svelte";
  import Icon from "@iconify/svelte";
  import Layout from "../components/Layout.svelte";
  import { t } from "../lib/i18n.svelte.js";
  import { api } from "../lib/api.svelte.js";

  let rawLines = $state([]);
  let host = $state(null);
  let started = $state(null);
  let limit = $state(500);
  let q = $state("");
  let paused = $state(false);
  let loading = $state(true);

  let lines = $derived(
    q ? rawLines.filter(l => l.line.toLowerCase().includes(q.toLowerCase())) : rawLines
  );

  function fmtTime(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    const h = String(d.getHours()).padStart(2, "0");
    const m = String(d.getMinutes()).padStart(2, "0");
    const s = String(d.getSeconds()).padStart(2, "0");
    const ms = String(d.getMilliseconds()).padStart(3, "0");
    return `${h}:${m}:${s}.${ms}`;
  }

  function fmtUptime(secs) {
    if (!secs) return "—";
    const d = Math.floor(secs / 86400);
    const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    return `${d}d ${h}h ${m}m ${s}s`;
  }

  async function fetchTail() {
    try {
      const data = await api.get(`/tail?limit=${limit}`);
      rawLines = data.lines || [];
      host = data.host;
      started = data.started;
      loading = false;
    } catch {
      loading = false;
    }
  }

  let interval;
  onMount(() => {
    fetchTail();
    interval = setInterval(() => {
      if (!paused && document.visibilityState !== "hidden") fetchTail();
    }, 1000);
    return () => clearInterval(interval);
  });
</script>

<Layout>
  <div class="ui-card p-5 sm:p-6">
    <!-- Header -->
    <div class="flex items-start gap-3 mb-6">
      <span class="ui-header-icon">
        <Icon icon="mdi:console" class="w-[18px] h-[18px]" />
      </span>
      <div class="flex-1">
        <h1 class="text-lg font-normal tracking-wide text-zinc-100">{t("nav.runtime")}</h1>
        <p class="mt-0.5 text-sm text-zinc-400">{t("runtime.subtitle")}</p>
      </div>
      <div class="flex items-center gap-2">
        <button class="ui-btn" onclick={() => { paused = !paused; if (!paused) fetchTail(); }}>
          <Icon icon={paused ? "mdi:play" : "mdi:pause"} class="w-4 h-4" />
          {paused ? t("runtime.resume") : t("runtime.pause")}
        </button>
        <button class="ui-btn" onclick={fetchTail}>
          <Icon icon="mdi:refresh" class="w-4 h-4" />
          {t("common.refresh")}
        </button>
      </div>
    </div>

    <!-- Host metrics -->
    {#if host}
      <div class="flex flex-wrap gap-3 mb-4">
        <div class="ui-btn pointer-events-none text-xs">
          <Icon icon="mdi:chip" class="w-4 h-4" />
          {t("runtime.cpu")} {host.cpu_percent?.toFixed(1)}%
        </div>
        <div class="ui-btn pointer-events-none text-xs">
          <Icon icon="mdi:memory" class="w-4 h-4" />
          {t("runtime.memory")} {host.memory_used_mb?.toFixed(1)} / {host.memory_total_mb?.toFixed(1)} MB
        </div>
        <div class="ui-btn pointer-events-none text-xs">
          <Icon icon="mdi:harddisk" class="w-4 h-4" />
          {t("runtime.disk")} {host.disk_used_gb?.toFixed(0)} / {host.disk_total_gb?.toFixed(0)} GB
        </div>
        <div class="ui-btn pointer-events-none text-xs">
          <Icon icon="mdi:clock-outline" class="w-4 h-4" />
          {t("runtime.uptime")} {fmtUptime(host.uptime_seconds)}
        </div>
      </div>
    {/if}

    <!-- Controls bar -->
    <div class="flex flex-wrap gap-3 items-center mb-4 p-3 rounded bg-black/20 border border-white/10">
      <div class="flex-1 min-w-[200px]">
        <input type="text" class="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600 focus:outline-none focus:border-white/25" placeholder={t("common.search_placeholder")} bind:value={q} />
      </div>
      <div class="flex items-center gap-2">
        <label class="text-xs text-zinc-500">{t("runtime.limit")}</label>
        <input type="number" class="w-20 bg-black/30 border border-white/10 rounded px-2 py-2 text-sm text-zinc-200 focus:outline-none focus:border-white/25" bind:value={limit} min="10" max="5000" />
      </div>
    </div>

    <!-- Application log -->
    <div class="border-t border-white/10 pt-4">
      <div class="flex items-center justify-between mb-3">
        <h2 class="text-sm font-semibold text-zinc-300 flex items-center gap-2">
          <Icon icon="mdi:text-box-outline" class="w-4 h-4" />
          {t("runtime.app_log")}
        </h2>
        <span class="text-xs text-zinc-500">{lines.length} {t("runtime.lines")}</span>
      </div>
      <div class="max-h-[65vh] overflow-y-auto font-mono text-xs leading-relaxed">
        {#if loading}
          <p class="text-zinc-500 text-center py-8">{t("common.loading")}</p>
        {:else if lines.length === 0}
          <p class="text-zinc-500 text-center py-8">{t("runtime.no_entries")}</p>
        {:else}
          {#each lines as entry}
            <div class="flex gap-3 py-0.5 px-2 hover:bg-white/[0.02] rounded text-zinc-300">
              <span class="text-zinc-600 shrink-0 select-none">{fmtTime(entry.time_utc)}</span>
              <span class="break-all">{entry.line}</span>
            </div>
          {/each}
        {/if}
      </div>
    </div>
  </div>
</Layout>
